// Turning thrown errors into something a technician can act on.
//
// The API client throws Error objects carrying `status` and `data`. Network
// failures throw the browser's own TypeError ("Load failed" on Safari,
// "Failed to fetch" on Chrome) — text that means nothing in a customer's
// kitchen. Every catch block that renders to a tech routes through
// friendlyError() so the message names the problem and the next action.

const NETWORK_HINTS = [
  'load failed',
  'failed to fetch',
  'networkerror',
  'network request failed',
  'the internet connection appears to be offline',
];

function looksLikeNetworkError(message) {
  const m = String(message || '').toLowerCase();
  return NETWORK_HINTS.some((hint) => m.includes(hint));
}

/**
 * Map any thrown value to a short, plain-language sentence.
 *
 * @param {unknown} err     the caught value
 * @param {string}  action  what the tech was doing, phrased to complete
 *                          "Couldn't ___" — e.g. "load your history"
 * @returns {string}
 */
export function friendlyError(err, action = 'complete that') {
  // AbortError (a request that hit its own timeout, e.g. sendFeedback's ~20s
  // clock) carries a browser-authored message like "Fetch is aborted" or
  // "signal is aborted without reason" — short and plain enough to pass the
  // `isOurs` check below and get shown to the tech verbatim if we don't
  // catch it here first.
  if (err && err.name === 'AbortError') {
    return 'That took too long. Check your signal and try again.';
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return `You're offline. Reconnect to Wi-Fi or cell data and try again.`;
  }

  const raw = err && err.message ? err.message : '';

  if (looksLikeNetworkError(raw)) {
    return `Couldn't reach the server. Check your signal and try again.`;
  }

  const status = err && err.status;
  if (status === 401 || status === 403) {
    return `You're not signed in anymore. Reload the app and sign in again.`;
  }
  if (status === 413) {
    return `That file is too large. Try a photo instead of a full PDF, or split it up.`;
  }
  if (status === 429) {
    return `The app is busy right now. Wait a few seconds and try again.`;
  }
  if (status === 404) {
    return `Couldn't ${action} — the server didn't find it. It may have been deleted.`;
  }
  if (status === 504) {
    return `The AI took too long to respond. Try again.`;
  }
  // A message the worker wrote on purpose (short, no stack-trace shape) is
  // more useful to the tech than our generic line. Browser-thrown errors
  // (TypeError, DOMException) are not — those get the fallback.
  const isOurs =
    raw &&
    raw.length < 120 &&
    !/^[A-Z][a-zA-Z]*Error\b/.test(raw) &&
    !raw.includes('undefined') &&
    !raw.includes('null');
  // 503 is the worker saying "not now, on purpose" (the daily AI budget):
  // its sentence tells the tech what to do; "try again" would not work.
  if (status === 503 && isOurs) return raw;
  if (typeof status === 'number' && status >= 500) {
    return `The server had a problem. Try again in a moment — if it keeps failing, tell Jason.`;
  }
  if (isOurs) return raw;

  return `Couldn't ${action}. Try again — if it keeps failing, tell Jason.`;
}
